'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useLocale, useTranslations } from 'next-intl';
import {
  ParagraphCollectionFragment,
  PropertiesTableFragment,
  RichTextFragment,
  SingleLineFragment,
} from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import { buildTrackingId } from '@/utils/tracking-helper';
import EmailHelper from '@/utils/email-helper';
import StringHelper from '@/utils/string-helper';
import Container from '@/ui/components/shared/Container';
import { ParagraphCollectionRenderer } from '@/ui/components/tileRenderers/ParagraphCollectionRenderer/ParagraphCollectionRenderer';
import { RichTextRenderer } from '@/ui/components/tileRenderers/RichTextRenderer';
import TextInput from '@/ui/components/shared/inputs/TextInput';
import Button from '@/ui/components/shared/Button';
import { submitBetaProgramForm } from './beta-program-form-actions';
import { BETA_PROGRAM_REGISTRATION_ERROR, BetaProgramAction } from './beta-program-form-types';

export type BetaProgramFormTile = {
  title: RichTextFragment;
  description: RichTextFragment;
  'added-success-message': ParagraphCollectionFragment;
  'removed-success-message': ParagraphCollectionFragment;
  'email-label': SingleLineFragment;
  'first-name-label': SingleLineFragment;
  'last-name-label': SingleLineFragment;
  'nuki-id-label': SingleLineFragment;
  'device-to-beta-program-option-labels': PropertiesTableFragment;
  'terms-label': RichTextFragment;
  'button-label'?: SingleLineFragment;
};

// Crystallize option keys for radio button labels
const ADD_OPTION_KEY = 'add-device-to-beta-program';
const REMOVE_OPTION_KEY = 'remove-device-from-beta-program';

const getOptionLabel = (
  table: PropertiesTableFragment | undefined,
  key: string,
): string | undefined =>
  table?.sections
    ?.flatMap((section) => section?.properties ?? [])
    .find((property) => property?.key === key)?.value ?? undefined;

interface BetaProgramFormProps {
  tile: BetaProgramFormTile;
  trackingPrefix?: string;
}

interface FormData {
  email: string;
  firstName: string;
  lastName: string;
  nukiId: string;
  action: BetaProgramAction;
  privacyPolicy: boolean;
}

export function BetaProgramForm({ tile, trackingPrefix }: Readonly<BetaProgramFormProps>) {
  const t = useTranslations('BetaProgramForm');
  const locale = useLocale();

  const [successAction, setSuccessAction] = useState<BetaProgramAction | null>(null);
  const [submissionError, setSubmissionError] = useState('');

  const optionLabels = tile['device-to-beta-program-option-labels'];
  const addOptionLabel = getOptionLabel(optionLabels, ADD_OPTION_KEY) ?? t('formOptionAdd');
  const removeOptionLabel =
    getOptionLabel(optionLabels, REMOVE_OPTION_KEY) ?? t('formOptionRemove');

  const validationSchema = yup.object({
    email: yup
      .string()
      .email(t('formInvalidEmail'))
      .test('has-tld', t('formInvalidEmail'), (value) => {
        if (!value) {
          return true;
        }

        return EmailHelper.isValid(value);
      })
      .required(t('formRequiredEmail')),
    firstName: yup
      .string()
      .max(50, t('formMaxLengthExceeded'))
      .required(t('formRequiredFirstName')),
    lastName: yup.string().max(50, t('formMaxLengthExceeded')).required(t('formRequiredLastName')),
    nukiId: yup
      .string()
      .test('is-hex', t('formInvalidNukiId'), (value) => {
        if (!value) {
          return true;
        }

        return StringHelper.isHex(value);
      })
      .required(t('formRequiredNukiId')),
    action: yup.mixed<BetaProgramAction>().oneOf(['add', 'remove']).required(),
    privacyPolicy: yup
      .boolean()
      .oneOf([true], t('formRequiredPrivacyPolicy'))
      .required(t('formRequiredPrivacyPolicy')),
  });

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
      nukiId: '',
      action: 'add',
      privacyPolicy: false,
    },
  });

  const onSubmit = async (data: FormData) => {
    setSubmissionError('');

    const result = await submitBetaProgramForm({
      nukiId: data.nukiId,
      email: data.email,
      firstName: data.firstName,
      lastName: data.lastName,
      action: data.action,
      language: locale,
    });

    if (result.success) {
      setSuccessAction(data.action);
      return;
    }

    if (result.error === BETA_PROGRAM_REGISTRATION_ERROR.INVALID_NUKI_ID) {
      setError('nukiId', { type: 'server', message: t('formInvalidNukiId') });
      return;
    }

    setSubmissionError(t('formErrorSubmissionFailed'));
  };

  if (successAction) {
    const successMessage =
      successAction === 'add' ? tile['added-success-message'] : tile['removed-success-message'];

    return (
      <Container className="overflow-hidden">
        <div className="mx-auto max-w-3xl text-center">
          <ParagraphCollectionRenderer
            content={successMessage}
            titleClassName="text-3xl leading-tight mb-5 max-w-xl mx-auto"
            bodyClassName="text-2xl max-w-3xl mx-auto"
            highlightTitlePunctuation
            removeParagraphMargins
          />
        </div>
      </Container>
    );
  }

  return (
    <Container className="overflow-hidden">
      <div className="mx-auto max-w-3xl rounded-lg p-6 sm:p-10">
        <div className="mx-auto mb-8 max-w-3xl text-center lg:mb-10">
          <RichTextRenderer
            blocks={tile.title}
            className="mx-auto mb-4 max-w-xl text-3xl font-bold leading-tight"
          />
          <RichTextRenderer blocks={tile.description} className="mx-auto max-w-3xl text-2xl" />
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="mx-auto max-w-lg space-y-8" noValidate>
          <TextInput
            id="email"
            register={register}
            errors={errors.email}
            label={tile['email-label']?.text ?? t('formLabelEmail')}
            placeholder={t('formPlaceholderEmail')}
            required
            type="email"
          />

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TextInput
              id="firstName"
              register={register}
              errors={errors.firstName}
              label={tile['first-name-label']?.text ?? t('formLabelFirstName')}
              placeholder={t('formPlaceholderFirstName')}
              required
            />
            <TextInput
              id="lastName"
              register={register}
              errors={errors.lastName}
              label={tile['last-name-label']?.text ?? t('formLabelLastName')}
              placeholder={t('formPlaceholderLastName')}
              required
            />
          </div>

          <TextInput
            id="nukiId"
            register={register}
            errors={errors.nukiId}
            label={tile['nuki-id-label']?.text ?? t('formLabelNukiId')}
            placeholder={t('formPlaceholderNukiId')}
            required
          />

          <fieldset className="space-y-2">
            <legend className="sr-only">{t('formLegendAction')}</legend>
            <label htmlFor="action-add" className="flex cursor-pointer items-center gap-2">
              <input
                id="action-add"
                type="radio"
                value="add"
                {...register('action')}
                className="h-4 w-4 cursor-pointer"
              />
              <span>{addOptionLabel}</span>
            </label>
            <label htmlFor="action-remove" className="flex cursor-pointer items-center gap-2">
              <input
                id="action-remove"
                type="radio"
                value="remove"
                {...register('action')}
                className="h-4 w-4 cursor-pointer"
              />
              <span>{removeOptionLabel}</span>
            </label>
          </fieldset>

          <div className="mb-16">
            <label htmlFor="privacyPolicy" className="flex cursor-pointer items-start gap-2">
              <input
                id="privacyPolicy"
                type="checkbox"
                {...register('privacyPolicy')}
                className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer"
                aria-describedby="privacyPolicy-error"
              />
              <span className="text-neutral-55">
                <RichTextRenderer blocks={tile['terms-label']} />
              </span>
            </label>
            {errors.privacyPolicy && (
              <p id="privacyPolicy-error" className="text-sm text-red">
                {errors.privacyPolicy.message}
              </p>
            )}
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={isSubmitting}
            loading={isSubmitting}
            data-tile-element-id={buildTrackingId(trackingPrefix, 'submit')}
          >
            {tile['button-label']?.text ?? t('formSubmitBtn')}
          </Button>

          {submissionError && <p className="text-center text-sm text-red">{submissionError}</p>}
        </form>
      </div>
    </Container>
  );
}
